import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { getRowId } from 'components/AgGrid/utils';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ChapterEnum } from 'gql/graphql';
import { SortEnumType, WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { Button, cnMixSpace, HeaderSearchBar, IconClose, useDebounce } from 'uikit';
import { getCheckedIdsArr } from 'utils';

import 'ag-grid-community/styles/ag-theme-alpine.css';
import type { TextFieldPropValue } from '@consta/uikit/TextField';
import type { GridReadyEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type React from 'react';
import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CreateMtrDialog } from '../CreateMtrDialog';
import {
    gqlAddMtrToActKC2PriceList,
    gqlAddMtrToWorkOrderFromPriceList,
    gqlWorkOrderMtrPriceList,
} from '../workOrderQueries';

import { CellRenderCheckbox } from './cellRender/CellRenderCheckbox';
import { getWhere } from './helpers';
import { useColumnsAddMtr } from './hooks/useColumnsAddMtr';

import './AddMtrDialog.css';

export interface AddMtrVariables {
    workOrderPriceId: number;
    chapter?: ChapterEnum | undefined;
}

export interface AddMtrDialogProps {
    shouldAddToActKC2: boolean;
    workOrderStatus?: WorkOrderStatusEnum;
    mtrId?: number;
    chapter?: ChapterEnum | null;
    closeModal?: () => void;
    onSuccess?: () => void;
}

export const AddMtrDialog: React.FC<AddMtrDialogProps> = (props) => {
    const { shouldAddToActKC2, workOrderStatus, mtrId: mtrIdProp, chapter: chapterProp, closeModal, onSuccess } = props;
    const { id: projectId, orderId, uerId: uerIdParam } = useParams();
    const location = useLocation();
    const state = location?.state as { selected: number; chapter: ChapterEnum } | undefined;
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const uerId = useMemo(() => (mtrIdProp ? String(mtrIdProp) : uerIdParam), [mtrIdProp, uerIdParam]);
    const chapter = useMemo(() => chapterProp ?? state?.chapter, [chapterProp, state?.chapter]);

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [valueNote, setValueNote] = useState<Record<string, string>>({});
    const [filters, setFilters] = useState({});
    const [searchString, setSearchString] = useState('');
    const [debounceSearchValue, setDebounceSearchValue] = useState<string>('');
    const tableRef = useRef<AgGridReact>(null);

    const variables = useMemo(
        () => ({
            workOrderPriceId: Number(uerId),
            take: 50,
            chapter,
            where: getWhere(filters),
            order: [{ code: SortEnumType.Asc }],
        }),
        [filters, chapter, uerId],
    );
    const filterVariables = useMemo(
        () => ({
            workOrderPriceId: Number(uerId),
            take: 1000,
            chapter,
            where: getWhere(filters),
            order: [{ code: SortEnumType.Asc }],
        }),
        [filters, chapter, uerId],
    );

    const [mtrPriceResponse, , , makeRequestMtrPrice] = useRequest({
        requestId: 'gqlWorkOrderMtrPriceList',
        query: gqlWorkOrderMtrPriceList,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'gqlWorkOrderMtrPriceList',
        requestFn: makeRequestMtrPrice,
        requestVariables: variables,
    });

    useEffect(() => {
        if (mtrPriceResponse?.uerWorkOrderPriceListMtr?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                mtrPriceResponse.uerWorkOrderPriceListMtr.items,
                mtrPriceResponse.uerWorkOrderPriceListMtr.totalCount,
            );
        }
    }, [
        getRowParams,
        mtrPriceResponse?.uerWorkOrderPriceListMtr?.items,
        mtrPriceResponse?.uerWorkOrderPriceListMtr?.totalCount,
    ]);

    const refreshMtrPriceResponse = useCallback(() => {
        tableRef?.current?.api?.refreshInfiniteCache();
    }, []);

    const [, addMtrResponseStatus, , addMtrToWorkOrder] = useRequest({
        requestId: 'addMtrToWorkOrderFromPriceList',
        // @ts-ignore
        query: shouldAddToActKC2 ? gqlAddMtrToActKC2PriceList : gqlAddMtrToWorkOrderFromPriceList,
        withEffect: false,
    });

    const handleClickOutside = useCallback(() => {
        setCheckedIds({});
        if (closeModal) {
            closeModal();
            navigate('./', { state: undefined });
        } else {
            navigate('../', { replace: true });
        }
    }, [closeModal, navigate]);

    const handleChangeCheckbox = useCallback(
        (rowId: number) =>
            ({ checked }: { checked: boolean }) => {
                setCheckedIds((ids) => ({ ...ids, [rowId]: checked }));
            },
        [],
    );

    const handleChangesValueNote = useCallback(
        (rowId: number) =>
            ({ value }: { value: TextFieldPropValue }) => {
                setValueNote((ids) => ({ ...ids, [rowId]: value }));
            },
        [],
    );

    const checkedIdsArr = useMemo(() => getCheckedIdsArr(checkedIds), [checkedIds]);

    const analyticsData = useMemo(
        () => ({
            projectId,
            orderId: Number(orderId),
            orderStatus: workOrderStatus,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        }),
        [
            orderId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            workOrderStatus,
        ],
    );

    const mtrInputs = useMemo(
        () =>
            Object.keys(checkedIds).map((mtrRowId) => ({
                mtrRowId: parseInt(mtrRowId, 10),
                rationale: valueNote[mtrRowId] || '',
            })),
        [checkedIds, valueNote],
    );

    const handleModalConfirm = useCallback(() => {
        if (!shouldAddToActKC2) {
            dispatch(
                addMtrToWorkOrder({
                    workOrderPriceId: Number(uerId),
                    mtrRowIds: checkedIdsArr,
                }),
            );

            sendGpnAnalyticsEvent({
                componentId: 'rim_NZ_add to order MTR button_pressed',
                ...analyticsData,
                addType: WorkOrderPriceTypeEnum.Mtr,
            });
        } else if (shouldAddToActKC2) {
            dispatch(
                addMtrToWorkOrder({
                    workOrderPriceId: Number(uerId),
                    mtrInputs,
                } as any),
            );

            sendGpnAnalyticsEvent({
                componentId: 'rim_act KC_add to order MTR button_pressed',
                ...analyticsData,
                addType: WorkOrderPriceTypeEnum.Mtr,
            });
        }
        setIsSubmitted(true);
    }, [
        addMtrToWorkOrder,
        analyticsData,
        checkedIdsArr,
        dispatch,
        mtrInputs,
        sendGpnAnalyticsEvent,
        shouldAddToActKC2,
        uerId,
    ]);

    useEffect(() => {
        if (isSubmitted && addMtrResponseStatus === RequestStatus.SUCCESS) {
            if (onSuccess) {
                onSuccess();
                navigate('./', { state: undefined });
            } else {
                navigate('../', { replace: true, state: { reload: Math.random() } });
            }
        }
    }, [addMtrResponseStatus, isSubmitted, navigate, onSuccess]);

    useEffect(() => {
        if (state?.selected) {
            setCheckedIds((ids) => ({ ...ids, [state?.selected]: true }));
        }
    }, [handleChangeCheckbox, state?.selected]);

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilters((prevValue) => ({
                ...prevValue,
                [fieldName]: checkedValues,
            }));
        },
        [],
    );

    const { columns } = useColumnsAddMtr({
        changeFilterHandler,
        variables: filterVariables,
        workOrderStatus,
        checkedIds,
        valueNote,
        handleChangesValueNote,
    });

    const onSearchDebouncedSearchValueChange = useDebounce((v) => {
        setDebounceSearchValue(v);
        setFilters((currentFilters) => ({ ...currentFilters, searchString: v }));
    }, 500);

    const handleChange = useCallback(
        ({ value }) => {
            setSearchString(value);
            onSearchDebouncedSearchValueChange(value);
        },
        [onSearchDebouncedSearchValueChange],
    );

    const handleClearSearch = useCallback(() => {
        setSearchString('');
        setDebounceSearchValue('');
        setFilters((prevFilters) => ({
            ...prevFilters,
            searchString: undefined,
        }));
    }, [setSearchString]);

    const finalColumns = useMemo(
        () => [
            {
                headerName: '',
                field: 'checkbox',
                width: 60,
                cellClass: 'firstColumn',
                spanHeaderHeight: true,
                autoHeight: true,
                cellRenderer: CellRenderCheckbox,
                cellRendererParams: {
                    checkedIds,
                    handleChangeCheckbox,
                },
            },
            ...columns,
        ],
        [checkedIds, columns, handleChangeCheckbox],
    );

    const isConfirmBtnDisabled = !checkedIdsArr.length;

    const onGridReady = useCallback((event: GridReadyEvent) => {
        event.api.sizeColumnsToFit();
    }, []);

    const btnConfirmLabel =
        workOrderStatus === WorkOrderStatusEnum.InProgress ? 'Добавить в Акт' : 'Добавить в Наряд-Заказ';

    const gridOptions = {
        headerHeight: 72,
    };

    return (
        <ModalDialog
            title="Добавление МТР из прейскуранта МТР"
            isOpen
            hasOverlay
            withTitleCloseButton
            onClose={handleClickOutside}
            onConfirm={handleModalConfirm}
            btnConfirmDisabled={isConfirmBtnDisabled}
            btnConfirmLabel={btnConfirmLabel}
            btnCancelLabel="Отменить"
            leftPanel={<CreateMtrDialog refreshMtrPriceResponse={refreshMtrPriceResponse} chapter={chapter} />}
            className={
                workOrderStatus === WorkOrderStatusEnum.InProgress
                    ? 'modalDialogAddMtr InProgress'
                    : 'modalDialogAddMtr'
            }
            isOnClickOutsideDisable
        >
            <div id="addMtrDialog">
                <div className={cnMixSpace({ mT: 'xl', mB: 'm' })} style={{ position: 'relative' }}>
                    <HeaderSearchBar
                        className="AddMtrSearch"
                        placeholder="Поиск МТР в прейскуранте"
                        label="поиск"
                        value={searchString}
                        onChange={handleChange}
                        onSearch={onSearchDebouncedSearchValueChange}
                    />
                    {searchString && (
                        <Button
                            label="Очистить"
                            view="ghost"
                            iconLeft={IconClose}
                            form="round"
                            size="xs"
                            onlyIcon
                            onClick={handleClearSearch}
                            style={{
                                position: 'absolute',
                                top: '50%',
                                right: '16px',
                                transform: 'translateY(-50%)',
                                zIndex: 1,
                            }}
                        />
                    )}
                </div>

                <div style={{ height: '500px' }} className="ag-theme-alpine gridWrapper">
                    <AgGridTable
                        key={debounceSearchValue}
                        ref={tableRef}
                        getRowId={getRowId}
                        datasource={dataSource}
                        columnDefs={finalColumns}
                        rowModelType="infinite"
                        maxConcurrentDatasourceRequests={1}
                        maxBlocksInCache={50}
                        cacheBlockSize={50}
                        onGridReady={onGridReady}
                        suppressCellFocus
                        gridOptions={gridOptions}
                    />
                </div>
            </div>
        </ModalDialog>
    );
};

AddMtrDialog.displayName = 'AddMtrDialog';
